import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { recommendations, videos } from "@/mocks/recommendations";

interface DoshaResult {
  vata: { score: number; percent: number };
  pitta: { score: number; percent: number };
  kapha: { score: number; percent: number };
  dominants: string[];
  dominant: string;
}

interface VideoItem {
  id: string;
  title: string;
  description: string;
  youtubeUrl: string;
  dosha: string;
  category: string;
}

const COLORS = {
  vata: "oklch(0.68 0.12 80)",
  pitta: "oklch(0.42 0.08 150)",
  kapha: "oklch(0.52 0.035 120)",
};

const VIDEO_THUMBNAILS: Record<string, string> = {
  vv1: "https://readdy.ai/api/search-image?query=Calm%20peaceful%20person%20meditating%20in%20a%20serene%20natural%20setting%20with%20soft%20warm%20golden%20light%2C%20ayurvedic%20healing%20aesthetic%2C%20minimalist%20composition%2C%20gentle%20earth%20tones%2C%20digital%20illustration%20style%20with%20clean%20lines%20and%20warm%20atmosphere&width=640&height=360&seq=video-vata-ansiedade&orientation=landscape",
  vv2: "https://readdy.ai/api/search-image?query=Morning%20sunrise%20yoga%20practice%20on%20a%20wooden%20deck%20overlooking%20nature%2C%20warm%20golden%20hour%20light%2C%20peaceful%20ayurvedic%20morning%20routine%2C%20earthy%20natural%20colors%2C%20minimalist%20digital%20illustration%20with%20soft%20atmospheric%20lighting&width=640&height=360&seq=video-vata-rotina&orientation=landscape",
  vv3: "https://readdy.ai/api/search-image?query=Warm%20nourishing%20soup%20and%20cooked%20vegetables%20on%20a%20wooden%20table%2C%20steam%20rising%2C%20soft%20natural%20kitchen%20lighting%2C%20ayurvedic%20healing%20food%20aesthetic%2C%20earthy%20warm%20tones%2C%20clean%20minimalist%20food%20photography%20style&width=640&height=360&seq=video-vata-alimentacao&orientation=landscape",
  vv4: "https://readdy.ai/api/search-image?query=Peaceful%20bedroom%20with%20candles%20and%20soft%20warm%20lighting%2C%20nighttime%20ritual%20setting%20with%20essential%20oils%2C%20calming%20ayurvedic%20sleep%20environment%2C%20cozy%20minimalist%20interior%2C%20muted%20earth%20tones%20and%20gentle%20ambient%20atmosphere&width=640&height=360&seq=video-vata-sono&orientation=landscape",
  vp1: "https://readdy.ai/api/search-image?query=Person%20practicing%20cooling%20breath%20pranayama%20in%20a%20serene%20garden%20with%20mint%20leaves%20and%20cool%20blue%20tones%2C%20ayurvedic%20pitta%20calming%20concept%2C%20sheetali%20breathing%20technique%2C%20soft%20cool%20colors%2C%20peaceful%20digital%20illustration&width=640&height=360&seq=video-pitta-raiva&orientation=landscape",
  vp2: "https://readdy.ai/api/search-image?query=Fresh%20cooling%20fruits%20and%20mint%20leaves%20on%20a%20light%20stone%20surface%2C%20cucumber%20melon%20coconut%2C%20refreshing%20ayurvedic%20food%20photography%2C%20soft%20natural%20lighting%2C%20clean%20minimalist%20composition%20with%20green%20and%20white%20tones&width=640&height=360&seq=video-pitta-alimentacao&orientation=landscape",
  vp3: "https://readdy.ai/api/search-image?query=Mindfulness%20meditation%20in%20a%20serene%20garden%20with%20soft%20natural%20light%2C%20lotus%20position%20silhouette%20against%20warm%20sunset%2C%20emotional%20control%20ayurvedic%20concept%2C%20peaceful%20atmosphere%2C%20digital%20illustration%20with%20gentle%20gradients&width=640&height=360&seq=video-pitta-mindfulness&orientation=landscape",
  vp4: "https://readdy.ai/api/search-image?query=Gentle%20cooling%20yoga%20practice%20in%20a%20bright%20airy%20studio%20with%20mint%20and%20sage%20green%20tones%2C%20ayurvedic%20pitta%20pacifying%20flow%2C%20soft%20neutral%20colors%20with%20cool%20accents%2C%20minimalist%20wellness%20photography%20style&width=640&height=360&seq=video-pitta-yoga&orientation=landscape",
  vk1: "https://readdy.ai/api/search-image?query=Energetic%20person%20running%20in%20nature%20at%20sunrise%2C%20dynamic%20morning%20light%2C%20ayurvedic%20vitality%20concept%2C%20warm%20golden%20tones%2C%20active%20lifestyle%20aesthetic%2C%20digital%20illustration%20with%20motion%20feeling%20and%20bright%20atmosphere&width=640&height=360&seq=video-kapha-energia&orientation=landscape",
  vk2: "https://readdy.ai/api/search-image?query=Organized%20workspace%20with%20natural%20light%20and%20plants%2C%20focused%20productive%20energy%2C%20ayurvedic%20lifestyle%20concept%2C%20clean%20minimalist%20desk%20setup%2C%20warm%20earthy%20tones%20with%20bright%20accents%2C%20modern%20wellness%20aesthetic&width=640&height=360&seq=video-kapha-produtividade&orientation=landscape",
  vk3: "https://readdy.ai/api/search-image?query=Vibrant%20spicy%20herbs%20and%20light%20vegetable%20dish%20on%20a%20dark%20wooden%20table%2C%20ginger%20pepper%20green%20tea%2C%20ayurvedic%20stimulating%20food%2C%20warm%20contrasting%20colors%2C%20clean%20food%20photography%20with%20rich%20textures&width=640&height=360&seq=video-kapha-alimentacao&orientation=landscape",
  vk4: "https://readdy.ai/api/search-image?query=Morning%20vinyasa%20yoga%20flow%20in%20a%20bright%20airy%20room%2C%20dynamic%20movement%20and%20stretching%2C%20ayurvedic%20kapha%20energizing%20practice%2C%20warm%20sunlight%20streaming%20through%20windows%2C%20energetic%20clean%20composition%2C%20modern%20wellness%20photography&width=640&height=360&seq=video-kapha-exercicios&orientation=landscape",
};

const DOSHA_INFO: Record<string, { label: string; desc: string; emoji: string }> = {
  vata: {
    label: "Vata — Ar + Espaço",
    emoji: "ri-windy-line",
    desc: "Desequilíbrio relacionado a ansiedade, agitação mental, insônia, dificuldade de concentração e oscilações mentais.",
  },
  pitta: {
    label: "Pitta — Fogo + Água",
    emoji: "ri-fire-line",
    desc: "Desequilíbrio relacionado a irritabilidade, inflamações, problemas digestivos, competição excessiva e tendência ao perfeccionismo.",
  },
  kapha: {
    label: "Kapha — Terra + Água",
    emoji: "ri-drop-line",
    desc: "Desequilíbrio relacionado a lentidão, ganho de peso, sonolência excessiva, resistência à mudança e acumulação física e mental.",
  },
};

export default function Resultado() {
  const navigate = useNavigate();
  const [result, setResult] = useState<DoshaResult | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeRec, setActiveRec] = useState("alimentacao");
  const [showEmailBanner, setShowEmailBanner] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);

  useEffect(() => {
    // Local payment flags are not proof of payment. Keep access closed until
    // the server can verify Mercado Pago approval and return the paid report.
    const paid = false;
    const saved = localStorage.getItem("dosha_test_result");
    if (!paid || !saved) {
      navigate("/resultado-bloqueado");
      return;
    }
    try {
      setResult(JSON.parse(saved));
    } catch {
      navigate("/teste");
    }
  }, [navigate]);

  const userEmail = localStorage.getItem("dosha_user_email") || "";

  if (!result) return null;

  const pieData = [
    { name: "Vata", value: result.vata.percent, color: COLORS.vata },
    { name: "Pitta", value: result.pitta.percent, color: COLORS.pitta },
    { name: "Kapha", value: result.kapha.percent, color: COLORS.kapha },
  ];

  const barData = [
    { name: "Vata", value: result.vata.percent },
    { name: "Pitta", value: result.pitta.percent },
    { name: "Kapha", value: result.kapha.percent },
  ];

  const dominantLabel = DOSHA_INFO[result.dominant].label;

  const dominantDesc =
    result.dominants.length === 1
      ? `Seu principal desequilíbrio é ${DOSHA_INFO[result.dominants[0]].label}. ${DOSHA_INFO[result.dominants[0]].desc}`
      : `Você apresenta um perfil misto. Os doshas ${result.dominants
          .map((d) => DOSHA_INFO[d].label)
          .join(" e ")} estão com pontuações muito próximas (diferença < 10%), indicando que ambos precisam de atenção no seu reequilíbrio.`;

  const recs = recommendations.filter(
    (r) => result.dominants.includes(r.dosha) && r.category === activeRec
  );

  const userVideos = videos.filter(
    (v) => result.dominants.includes(v.dosha)
  );

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <div className="bg-primary-500 py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 font-body text-sm text-background-200/70 hover:text-background-50 transition-colors mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar ao início
          </button>
          <h1 className="font-heading text-3xl md:text-4xl text-background-50">
            Seu Perfil Dosha
          </h1>
          <p className="font-body text-background-200/70 mt-2">
            Resultado do Teste dos Doshas
          </p>
        </div>
      </div>

      {/* Email confirmation banner */}
      {showEmailBanner && userEmail && (
        <div className="bg-accent-100 border-b border-accent-200">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <i className="ri-mail-check-line text-accent-600 text-lg"></i>
              <p className="font-body text-sm text-accent-800">
                Resultado enviado para <strong>{userEmail}</strong> — confira sua caixa de entrada!
              </p>
            </div>
            <button
              onClick={() => setShowEmailBanner(false)}
              className="text-accent-600 hover:text-accent-800 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: "overview", label: "Visão Geral", icon: "ri-dashboard-line" },
            { id: "report", label: "Relatório", icon: "ri-file-text-line" },
            { id: "videos", label: "Vídeos", icon: "ri-video-line" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-secondary-500 hover:text-foreground-950"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
                <h3 className="font-heading text-foreground-950 text-lg mb-4">
                  Distribuição dos Doshas
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {pieData.map((d) => (
                    <div key={d.name} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: d.color }}
                      />
                      <span className="font-body text-sm text-secondary-500">
                        {d.name} {d.value}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bar Chart */}
              <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
                <h3 className="font-heading text-foreground-950 text-lg mb-4">
                  Percentuais
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" />
                    <XAxis dataKey="name" tick={{ fontSize: 14, fontFamily: "var(--font-body)" }} />
                    <YAxis tick={{ fontSize: 12, fontFamily: "var(--font-body)" }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="oklch(var(--accent-500))" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Dominant Dosha Section */}
            {result.dominants.length === 1 ? (
              <div className="bg-primary-500 rounded-2xl p-8 text-center">
                <span className="inline-block text-accent-500 font-body text-sm tracking-[0.25em] uppercase mb-3">
                  Dosha Predominante
                </span>
                <h2 className="font-heading text-3xl md:text-4xl text-background-50 mb-4">
                  {DOSHA_INFO[result.dominants[0]].label}
                </h2>
                <p className="font-body text-background-200/70 text-lg leading-relaxed max-w-2xl mx-auto">
                  {DOSHA_INFO[result.dominants[0]].desc}
                </p>
              </div>
            ) : (
              <div>
                <div className="text-center mb-6">
                  <span className="inline-block text-accent-500 font-body text-sm tracking-[0.25em] uppercase mb-2">
                    Perfil Misto
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    Seus Doshas Predominantes
                  </h2>
                  <p className="font-body text-secondary-500 text-sm mt-2">
                    Diferença entre eles é menor que 10% — ambos influenciam seu equilíbrio
                  </p>
                </div>
                <div className={`grid gap-4 ${result.dominants.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}>
                  {result.dominants.map((d) => (
                    <div key={d} className="bg-primary-500 rounded-2xl p-6 text-center">
                      <div className="w-14 h-14 rounded-full bg-background-50/20 flex items-center justify-center mx-auto mb-3">
                        <i className={`${DOSHA_INFO[d].emoji} text-background-50 text-2xl`}></i>
                      </div>
                      <h3 className="font-heading text-xl text-background-50 mb-3">
                        {DOSHA_INFO[d].label}
                      </h3>
                      <p className="font-body text-sm text-background-200/70 leading-relaxed">
                        {DOSHA_INFO[d].desc}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-5 mt-6">
                  <p className="font-body text-sm text-accent-800 leading-relaxed">
                    {dominantDesc}
                  </p>
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: "vata", label: "Vata", value: `${result.vata.percent}%`, color: "text-accent-500", bg: "bg-accent-500/10", desc: "Ar + Espaço" },
                { key: "pitta", label: "Pitta", value: `${result.pitta.percent}%`, color: "text-primary-500", bg: "bg-primary-500/10", desc: "Fogo + Água" },
                { key: "kapha", label: "Kapha", value: `${result.kapha.percent}%`, color: "text-secondary-500", bg: "bg-secondary-500/10", desc: "Terra + Água" },
              ].map((stat) => {
                const isDominant = result.dominants.includes(stat.key);
                return (
                <div
                  key={stat.label}
                  className={`${isDominant ? "bg-primary-500" : stat.bg} rounded-2xl p-6 text-center border ${isDominant ? "border-primary-400" : "border-primary-500/5"} transition-all`}
                >
                  {isDominant && (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-background-50/20 text-background-50 font-body text-[10px] tracking-wider uppercase mb-2">
                      Dominante
                    </span>
                  )}
                  <span className={`font-body text-sm ${isDominant ? "text-background-200/70" : "text-secondary-500"} uppercase tracking-wider`}>
                    {stat.label}
                  </span>
                  <span
                    className={`block font-heading text-4xl ${isDominant ? "text-background-50" : stat.color} my-2`}
                  >
                    {stat.value}
                  </span>
                  <span className={`font-body text-xs ${isDominant ? "text-background-200/50" : "text-secondary-400"}`}>
                    {stat.desc}
                  </span>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Report Tab */}
        {activeTab === "report" && (
          <div className="space-y-6">
            {/* Diagnosis */}
            <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
              <h3 className="font-heading text-foreground-950 text-xl mb-4">
                Diagnóstico Personalizado
              </h3>
              <p className="font-body text-secondary-500 leading-relaxed mb-6">
                {dominantDesc}
              </p>
              {result.dominants.length > 1 && (
                <div className="space-y-3 mb-6">
                  {result.dominants.map((d) => (
                    <div key={d} className="bg-primary-50 rounded-xl p-4 border border-primary-100">
                      <div className="flex items-center gap-2 mb-1">
                        <i className={`${DOSHA_INFO[d].emoji} text-primary-500`}></i>
                        <span className="font-heading text-sm text-primary-700 font-medium">
                          {DOSHA_INFO[d].label}
                        </span>
                      </div>
                      <p className="font-body text-sm text-primary-600">
                        {DOSHA_INFO[d].desc}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-primary-50 rounded-xl p-4">
                <p className="font-body text-sm text-primary-700">
                  <strong>Sua pontuação:</strong> Vata {result.vata.score} pontos,
                  Pitta {result.pitta.score} pontos, Kapha {result.kapha.score}{" "}
                  pontos (de um total de 135 pontos por dosha).
                </p>
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
              <h3 className="font-heading text-foreground-950 text-xl mb-4">
                Recomendações Personalizadas
              </h3>

              {/* Rec tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {[
                  { id: "alimentacao", label: "Alimentação", icon: "ri-restaurant-line" },
                  { id: "rotina", label: "Rotina", icon: "ri-calendar-check-line" },
                  { id: "mental", label: "Mental", icon: "ri-heart-line" },
                  { id: "estilo_vida", label: "Estilo de Vida", icon: "ri-sun-line" },
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveRec(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm whitespace-nowrap transition-all cursor-pointer ${
                      activeRec === cat.id
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200/50 text-secondary-500 hover:text-foreground-950"
                    }`}
                  >
                    <i className={cat.icon}></i>
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {recs.map((rec) => (
                  <div
                    key={rec.id}
                    className="border border-primary-500/5 rounded-xl p-5 hover:shadow-md transition-all"
                  >
                    <h4 className="font-heading text-foreground-950 text-lg mb-2">
                      {rec.title}
                    </h4>
                    <p className="font-body text-secondary-500 text-sm leading-relaxed">
                      {rec.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === "videos" && (
          <div className="space-y-6">
            <div className="bg-primary-500 rounded-2xl p-6 text-center">
              <h3 className="font-heading text-background-50 text-xl mb-2">
                Biblioteca de Vídeos Personalizada
              </h3>
              <p className="font-body text-background-200/70 text-sm">
                Conteúdo selecionado para{" "}
                {result.dominants.length === 1
                  ? `perfil ${DOSHA_INFO[result.dominants[0]].label}`
                  : `perfis ${result.dominants.map((d) => DOSHA_INFO[d].label).join(" + ")}`}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {userVideos.map((video) => (
                <div
                  key={video.id}
                  onClick={() => setSelectedVideo(video)}
                  className="bg-background-100 rounded-2xl overflow-hidden border border-primary-500/5 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                >
                  {/* Thumbnail */}
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={VIDEO_THUMBNAILS[video.id] || "https://readdy.ai/api/search-image?query=Ayurvedic%20wellness%20lifestyle%20concept%20with%20warm%20natural%20lighting%2C%20peaceful%20healing%20atmosphere%2C%20earthy%20tones%2C%20minimalist%20digital%20illustration%20with%20soft%20composition&width=640&height=360&seq=video-fallback&orientation=landscape"}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Play button overlay */}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-background-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-play-fill text-primary-500 text-2xl ml-0.5"></i>
                      </div>
                    </div>
                    {/* Duration badge */}
                    <div className="absolute bottom-3 right-3 px-2 py-1 rounded-md bg-black/60 text-background-50 font-body text-xs">
                      <i className="ri-time-line mr-1"></i>Assistir
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 rounded-full bg-accent-100 text-accent-700 font-body text-xs capitalize">
                        {video.dosha}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-background-200/70 text-secondary-500 font-body text-xs capitalize">
                        {video.category === "alimentacao" ? "Alimentação" : video.category === "rotina" ? "Rotina" : video.category === "mental" ? "Mental" : "Estilo de Vida"}
                      </span>
                    </div>
                    <h4 className="font-heading text-foreground-950 text-lg mb-1 group-hover:text-primary-500 transition-colors">
                      {video.title}
                    </h4>
                    <p className="font-body text-sm text-secondary-500 leading-relaxed">
                      {video.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="bg-background-50 rounded-2xl overflow-hidden w-full max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between p-5 border-b border-background-200/60">
              <div>
                <h3 className="font-heading text-foreground-950 text-lg">
                  {selectedVideo.title}
                </h3>
                <p className="font-body text-sm text-secondary-500 mt-0.5">
                  {selectedVideo.description}
                </p>
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-foreground-950 text-lg"></i>
              </button>
            </div>
            {/* Video player */}
            <div className="aspect-video w-full bg-black">
              <iframe
                src="https://drive.google.com/file/d/1IDshB2ylZsbABX1cv2YyZiJKRKyzt9eU/preview"
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            {/* Modal footer */}
            <div className="p-5 flex items-center justify-between border-t border-background-200/60">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-full bg-accent-100 text-accent-700 font-body text-xs capitalize">
                  {selectedVideo.dosha}
                </span>
                <span className="px-2 py-1 rounded-full bg-background-200/70 text-secondary-500 font-body text-xs capitalize">
                  {selectedVideo.category === "alimentacao" ? "Alimentação" : selectedVideo.category === "rotina" ? "Rotina" : selectedVideo.category === "mental" ? "Mental" : "Estilo de Vida"}
                </span>
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="px-6 py-2.5 rounded-full bg-background-100 text-secondary-500 font-body text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
